import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Page, Header } from 'components';
import { AddCircleOutline, AddCircleOutlined, AddCircleOutlineRounded, AddCircleSharp } from '@mui/icons-material';
import { Divider, Grid, colors, ListSubheader, Paper, Button, IconButton,
	List, ListItemButton, ListItemIcon, ListItemText, ListItem, Tooltip,
	TableContainer, Table, TableCell, TableRow, TableBody, TableHead } from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import IncidentStatusTable from './components/IncidentStatusTable';
import IncidentPriorityTable from './components/IncidentPriorityTable';
import IncidentGenericTypeTable from './components/IncidentGenericTypeTable';
import IncidentTypeTable from './components/IncidentTypeTable';
import IncidentDetailedTypeTable from './components/IncidentDetailedTypeTable';
import IncidentTechnologyTable from './components/IncidentTechnologyTable';
import IncidentLayersTable from './components/IncidentLayersTable';
import IncidentConfigurationDialog from './components/IncidentConfigurationDialog';

import {
	ToggleIncConfigDialog,
	GetIncidentConfigurations,
	SelectIncidentConfiguration,
	SetFormState
} from '../../actions/IncidentTrackerConfigurationActions.js';

import IncidentConfigurationStatusForm from './components/IncidentConfigurationStatusForm';
import IncidentConfigurationPriorityForm from './components/IncidentConfigurationPriorityForm';
import IncidentConfigurationGenericTypeForm from './components/IncidentConfigurationGenericTypeForm';
import IncidentConfigurationDetailedTypeForm from './components/IncidentConfigurationDetailedTypeForm';
import IncidentConfigurationTechnologyForm from './components/IncidentConfigurationTechnologyForm';
import IncidentConfigurationTypeForm from './components/IncidentConfigurationTypeForm';
import IncidentConfigurationLayerForm from './components/IncidentConfigurationLayerForm';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(3)
	},
	container: {
		marginTop: theme.spacing(3)
	},
	tabs: {
		marginTop: theme.spacing(3)
	},
	content: {
		marginTop: theme.spacing(3)
	},
	paper: {
		padding: theme.spacing(1)
	},
	popover: {
		pointerEvents: 'none'
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900]
		}
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
}));

const configs = [
	{
		name: 'status',
		label: 'Status',
		level: 0
	},
	{
		name: 'priority',
		label: 'Priority',
		level: 0
	},
	{
		name: 'genericType',
		label: 'Generic Type',
		level: 0
	},
	{
		name: 'type',
		label: 'Type',
		level: 1
	},
	{
		name: 'detailedType',
		label: 'Detailed Type',
		level: 2
	},
	{
		name: 'technology',
		label: 'Technology',
		level: 0
	},
	{
		name: 'layer',
		label: 'Layer',
		level: 1
	},
];

const IncidentTrackerConfiguration = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [selectedConfigCategory, setSelectedConfigCategory] = useState('genericType');
	const [selectedConfigCategoryLabel, setSelectedConfigCategoryLabel] = useState('Generic Type');
	const incidentConfigurations = useSelector((state) => state.incidentTrackerConfigurationReducer.incidentConfigurations);
	const incConfigDialogVisibility = useSelector((state) => state.incidentTrackerConfigurationReducer.incConfigDialogVisibility);
	const incConfigDialogMode = useSelector((state) => state.incidentTrackerConfigurationReducer.incConfigDialogMode);
	const selectedConfiguration = useSelector((state) => state.incidentTrackerConfigurationReducer.selectedConfiguration);
	const setFormState = formState => dispatch(SetFormState(formState));

	const _incConfigAddDialogOpen = (configuration, categoryLabel) => {
		setSelectedConfigCategoryLabel(categoryLabel);
		setSelectedConfigCategory(configuration.category);
		dispatch(SelectIncidentConfiguration(configuration));
		dispatch(ToggleIncConfigDialog(true, 'Add'));
	};

	const _handleSelectedConfigChange = (category, item) => {
		dispatch(SelectIncidentConfiguration({ category, item }));
	};

	const _incConfigDialogClose = () => {
		setFormState({
			isValid: false,
			values: {},
			touched: {},
			errors: {},
		});
		dispatch(ToggleIncConfigDialog(false, 'Add'));
	};

	const _handleConfigCategoryChange = (configuration, categoryLabel) => {
		setSelectedConfigCategoryLabel(categoryLabel);
		dispatch(SelectIncidentConfiguration(configuration));
		setSelectedConfigCategory(configuration.category);
	};

	useEffect(() => {
		dispatch(GetIncidentConfigurations());
	}, []);

	return (
		<Page className={classes.root} title="Incident Tracker Configuration">
			<Header module="Incident Tracker" view="Incident Tracker Configuration" />
			<Grid className={classes.container} container spacing={1}>
				<Grid item lg={3} xs={3}>
					<TableContainer component={Paper}>
						<Table size="small" aria-label="Configurations">
							<TableHead>
								<TableRow>
									<TableCell>Configurations</TableCell>
									<TableCell> </TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{configs.map(conf => (
									<TableRow>
										<TableCell padding="none">
											<ListItemButton sx={{ pl: 3 * (conf.level + 1) }} onClick={() => _handleConfigCategoryChange({ category: conf.name }, conf.label)}>
												{conf.level !== 0 && (
													<ListItemIcon>
														<SubdirectoryArrowRightIcon />
													</ListItemIcon>
												)}
												<ListItemText primary={conf.label} />
											</ListItemButton>
										</TableCell>
										<TableCell sx={{ pl: 1, pr: 1 }}>
											<Tooltip title="Add">
												<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _incConfigAddDialogOpen({ category: conf.name }, conf.label)}>
													<AddCircleOutlineRounded />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
				<Grid item lg={9} xs={9}>
					{selectedConfigCategory === 'status' && <IncidentStatusTable className={classes.results} data={incidentConfigurations.status} />}
					{selectedConfigCategory === 'priority' && <IncidentPriorityTable className={classes.results} data={incidentConfigurations.priorities} />}
					{selectedConfigCategory === 'genericType' && <IncidentGenericTypeTable className={classes.results} data={incidentConfigurations.genericTypes} />}
					{selectedConfigCategory === 'type' && <IncidentTypeTable className={classes.results} data={incidentConfigurations.types} />}
					{selectedConfigCategory === 'detailedType' && <IncidentDetailedTypeTable className={classes.results} data={incidentConfigurations.detailedTypes} />}
					{selectedConfigCategory === 'technology' && <IncidentTechnologyTable className={classes.results} data={incidentConfigurations.technologies} />}
					{selectedConfigCategory === 'layer' && <IncidentLayersTable className={classes.results} data={incidentConfigurations.layers} />}

					{incConfigDialogVisibility && (
						<IncidentConfigurationDialog
							category={selectedConfigCategoryLabel}
							onClose={_incConfigDialogClose}
							open={incConfigDialogVisibility}
							mode={incConfigDialogMode}
						>
							{selectedConfigCategory === 'status' && (
								<IncidentConfigurationStatusForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'priority' && (
								<IncidentConfigurationPriorityForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'genericType' && (
								<IncidentConfigurationGenericTypeForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'type' && (
								<IncidentConfigurationTypeForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									genericTypes={incidentConfigurations.genericTypes.map(genericType => ({ text: genericType.genericType, value: genericType.id }))}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'detailedType' && (
								<IncidentConfigurationDetailedTypeForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									genericTypes={incidentConfigurations.genericTypes.map(genericType => ({ text: genericType.genericType, value: genericType.id }))}
									types={incidentConfigurations.types.map(type => ({ text: type.type, value: type.id }))}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'technology' && (
								<IncidentConfigurationTechnologyForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
								/>
							)}
							{selectedConfigCategory === 'layer' && (
								<IncidentConfigurationLayerForm
									disabled={incConfigDialogMode === 'Delete'}
									defaultConfig={selectedConfiguration?.item}
									onChange={(val) => _handleSelectedConfigChange(selectedConfigCategory, val)}
									technologies={incidentConfigurations.technologies.map(tech => ({ text: tech.technology, value: tech.id }))}
								/>
							)}

						</IncidentConfigurationDialog>
					)}
				</Grid>
			</Grid>
		</Page>
	);
};

export default IncidentTrackerConfiguration;
